/**
 * designTree — build a folder/file tree from staged designs' relative paths
 * (DesignInput.bytesRef, populated by pickDirectory on both platforms). Pure &
 * DOM-free so the tree + tri-state cascade logic is unit-tested without React.
 */
import type { DesignInput } from "@genz/platform";

export interface FolderNode {
  kind: "folder";
  name: string;
  /** Slash-joined path from the root ("" for the root folder). */
  path: string;
  children: TreeNode[];
}
export interface FileNode {
  kind: "file";
  name: string;
  path: string;
  designId: string;
  /** Human design code (filename base sans extension) — see DesignInput.code. */
  code?: string;
}
export type TreeNode = FolderNode | FileNode;

/** Split a relative path into clean segments (handles "/" and "\", trims blanks). */
function segments(path: string): string[] {
  return path.split(/[\\/]+/).filter(Boolean);
}

export function buildDesignTree(designs: DesignInput[]): FolderNode {
  const root: FolderNode = { kind: "folder", name: "", path: "", children: [] };
  for (const d of designs) {
    const rel = d.bytesRef && d.bytesRef.trim() ? d.bytesRef : d.filename;
    const segs = segments(rel);
    // Last segment is the file; anything before is folders.
    const fileName = segs.length > 0 ? segs[segs.length - 1] : d.filename;
    const folders = segs.slice(0, -1);
    let cursor = root;
    let acc = "";
    for (const folder of folders) {
      acc = acc ? `${acc}/${folder}` : folder;
      let next = cursor.children.find(
        (c): c is FolderNode => c.kind === "folder" && c.name === folder,
      );
      if (!next) {
        next = { kind: "folder", name: folder, path: acc, children: [] };
        cursor.children.push(next);
      }
      cursor = next;
    }
    cursor.children.push({
      kind: "file",
      name: fileName,
      path: acc ? `${acc}/${fileName}` : fileName,
      designId: d.designId,
      code: d.code,
    });
  }
  return root;
}

/** Every descendant file's designId (a file node yields just itself). */
export function fileIdsUnder(node: TreeNode): string[] {
  if (node.kind === "file") return [node.designId];
  return node.children.flatMap(fileIdsUnder);
}

/** Tri-state for a node against the current selection set. A file is checked/unchecked;
 *  a folder is checked (all under it on), unchecked (none), or indeterminate (some). */
export function folderState(
  node: TreeNode,
  selected: Set<string>,
): "checked" | "indeterminate" | "unchecked" {
  const ids = fileIdsUnder(node);
  if (ids.length === 0) return "unchecked";
  const on = ids.filter((id) => selected.has(id)).length;
  if (on === 0) return "unchecked";
  if (on === ids.length) return "checked";
  return "indeterminate";
}
